import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import Monogram from "@/components/Monogram";
import api from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).finally(() => setLoading(false));
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <>
      <section className="pt-44 pb-16 px-6 lg:px-14 bg-[#F5F2ED] relative overflow-hidden">
        <Monogram size={760} opacity={0.05} color="#4A3E39" className="absolute -right-60 top-0 hidden md:block" />
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="eyebrow mb-6">The Ivory Edit</div>
            <h1 className="font-display font-light text-ivory-800 text-6xl md:text-8xl lg:text-[120px] leading-[0.95] max-w-5xl">
              Notes on a <em className="italic">quieter</em> beauty.
            </h1>
            <p className="mt-10 max-w-xl text-[15px] leading-[1.9] text-ivory-700">
              A small editorial, published slowly. Essays on hair, skin, ritual, and
              the rooms in which we make ourselves up.
            </p>
          </Reveal>
        </div>
      </section>

      {loading ? (
        <div className="px-6 lg:px-14 py-32 text-center text-ivory-700" data-testid="journal-loading">
          Loading the edit…
        </div>
      ) : (
        <>
          {/* Featured article */}
          {featured && (
            <section className="px-6 lg:px-14 py-20 lg:py-32 bg-[#F5F2ED]">
              <Link
                to={`/journal/${featured.slug}`}
                data-testid={`journal-featured-${featured.slug}`}
                className="block max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center group"
              >
                <div className="lg:col-span-7 zoom-wrap aspect-[4/5] overflow-hidden">
                  <img src={featured.hero_image} alt={featured.title} className="zoom-img w-full h-full object-cover" />
                </div>
                <div className="lg:col-span-5 lg:pl-6">
                  <div className="eyebrow mb-5">Featured · {featured.category}</div>
                  <h2 className="font-display font-light text-ivory-800 text-5xl md:text-6xl leading-[1.05]">
                    {featured.title}
                  </h2>
                  {featured.subtitle && (
                    <p className="mt-5 font-display italic text-ivory-700 text-xl md:text-2xl leading-snug">
                      {featured.subtitle}
                    </p>
                  )}
                  <p className="mt-8 text-[15px] leading-[1.9] text-ivory-700 max-w-md">
                    {featured.excerpt}
                  </p>
                  <div className="mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ivory-800 underline underline-offset-[8px] decoration-[#B7AB9B] group-hover:decoration-[#6D1F2B]">
                    Read the essay <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            </section>
          )}

          {/* Grid of remaining articles */}
          <section className="px-6 lg:px-14 pb-32 bg-[#F5F2ED]">
            <div className="max-w-[1500px] mx-auto">
              <div className="eyebrow mb-10">All Editions</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
                {rest.map((a, i) => (
                  <Reveal key={a.id} delay={i * 80}>
                    <Link to={`/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`} className="block group">
                      <div className="zoom-wrap aspect-[4/5] overflow-hidden">
                        <img src={a.hero_image} alt={a.title} className="zoom-img w-full h-full object-cover" />
                      </div>
                      <div className="mt-6">
                        <div className="eyebrow text-ivory-700">{a.category} · {a.reading_time || 4} min</div>
                        <h3 className="mt-3 font-display text-ivory-800 text-2xl md:text-3xl leading-tight group-hover:text-[#6D1F2B] transition-colors">
                          {a.title}
                        </h3>
                        {a.subtitle && (
                          <p className="mt-2 text-[14px] leading-relaxed text-ivory-700">{a.subtitle}</p>
                        )}
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
